// src/views/adminView/contractsView/compliance/CumplimientoEmpresaBody.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Cuerpo del expediente de cumplimiento de una empresa (contract real):
// banner de estado, cuenta corriente (fallback sin backend), carpeta
// documental, checklist contra la plantilla Chile, evaluaciones crediticias y
// usuarios del portal. Todo el estado documental es front-only (localStorage).
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  BadgeCheck,
  Ban,
  CalendarClock,
  CheckCircle2,
  CreditCard,
  FileSignature,
  FileText,
  Gavel,
  RefreshCw,
  Send,
  Upload,
  UserCheck,
} from 'lucide-react';
import { cn } from '../../../../utils/cn';
import { formatCLP, formatDate } from '../../../../utils/formatters';
import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import { Card, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/Card';
import { Drawer } from '../../../../components/ui/Drawer';
import { KV } from '../../../../components/ui/KV';
import { PendingBackendNotice } from '../../../../components/ui/PendingBackendNotice';
import { Table, TBody, TD, TH, THead, TR } from '../../../../components/ui/Table';
import {
  cuentaDe,
  motivosDe,
  resumenDeContract,
  textoRegla,
  DOC_CONTRATO,
  ESTADO_CUMPLIMIENTO_LABEL,
  PLANTILLA_NOMBRE,
} from './complianceUtils';
import { useComplianceDocs } from './useComplianceStore';
import { CumplimientoBar, CreditoBar, VencimientoPill } from './ComplianceShared';
import UsuariosEmpresaSection from './UsuariosEmpresaSection';
import SignedContractsList from '../SignedContractsList';

const BANNER_STYLES = {
  al_dia: 'border-status-success/30 bg-status-success-soft text-status-success',
  por_vencer: 'border-status-warning/30 bg-status-warning-soft text-status-warning',
  incompleto: 'border-status-info/30 bg-status-info-soft text-status-info',
  bloqueado: 'border-status-error/30 bg-status-error-soft text-status-error',
};

// Bajadas del banner por estado agregado (claves i18n).
const BANNER_BAJADA = {
  al_dia: 'Sin documentos vencidos ni bloqueos: el cliente opera con normalidad.',
  por_vencer:
    'Hay documentos próximos a vencer: conviene solicitar la renovación antes del corte.',
  incompleto: 'Falta documentación crítica para operar con cuenta corriente.',
  // Nada retiene despachos ni fuerza prepago: el texto no promete un bloqueo que no existe.
  bloqueado:
    'Hay documentos obligatorios vencidos. Renueva el contrato o actualiza la documentación para regularizar la carpeta.',
};

function AvisoCumplimiento({ resumen, motivos }) {
  const { t } = useTranslation();
  const Icon = resumen.estado === 'al_dia' ? CheckCircle2 : AlertTriangle;
  return (
    <div
      className={cn(
        'rounded-xl border px-4 py-3',
        BANNER_STYLES[resumen.estado] ?? BANNER_STYLES.incompleto,
      )}
      role="status"
    >
      <div className="flex items-start gap-2.5">
        <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <div className="min-w-0">
          <div className="text-sm font-semibold">
            {t(ESTADO_CUMPLIMIENTO_LABEL[resumen.estado] ?? resumen.estado)}
          </div>
          <p className="mt-0.5 text-sm opacity-90">{t(BANNER_BAJADA[resumen.estado] ?? '')}</p>
          {motivos.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs">
              {motivos.map((m) => (
                <li key={m} className="flex items-start gap-1.5">
                  <span
                    className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-current"
                    aria-hidden="true"
                  />
                  <span>{m}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// `onRenewContract` abre el modal "Renovar contrato" del detalle; sin él (rol sin permiso, o
// montado fuera del detalle) la fila del contrato marco no ofrece la acción.
export default function CumplimientoEmpresaBody({
  contract,
  onToast,
  sinAviso,
  onGoCuentas,
  onRenewContract,
}) {
  const { t } = useTranslation();
  const contractId = contract?._id;
  const { stored, setDocEstado, marcarSolicitado } = useComplianceDocs(contractId);
  const [verContrato, setVerContrato] = useState(false);

  // Recalculado en cada mutación del store → banner, barras y badges cambian
  // en vivo al aprobar/subir/rechazar.
  const resumen = useMemo(() => resumenDeContract(contract, stored), [contract, stored]);
  const motivos = motivosDe(resumen, t);
  const cuenta = cuentaDe(contractId);
  const contacto = resumen.empresa.contacto;
  const anio = new Date().getFullYear();

  const contratoReal = !!(contract?.dateSigned || contract?.validityPeriod?.startDate);
  const claseTone =
    cuenta.clasificacion === 'A' ? 'success' : cuenta.clasificacion === 'B' ? 'warning' : 'neutral';

  const toast = (msg) => {
    if (typeof onToast === 'function') onToast(msg);
  };

  const aprobar = (item) => {
    setDocEstado(item.docTipoId, 'vigente');
    toast(
      `${item.nombre} — ${t('Documento aprobado: queda vigente según la regla de la plantilla.')}`,
    );
  };

  const rechazar = (item) => {
    setDocEstado(item.docTipoId, 'rechazado');
    toast(`${item.nombre} — ${t('Documento rechazado: se solicita nuevamente al cliente.')}`);
  };

  const solicitar = (item) => {
    marcarSolicitado(item.docTipoId);
    toast(
      t('Solicitud de "{{nombre}}" enviada a {{destino}}.', {
        nombre: item.nombre,
        destino: contacto?.email ?? t('la empresa'),
      }),
    );
  };

  const subir = (item) => {
    setDocEstado(item.docTipoId, 'en_revision');
    toast(`${item.nombre} — ${t('Documento cargado y enviado a validación.')}`);
  };

  const iniciarEvaluacion = () => {
    toast(
      t(
        'Evaluación {{anio}} iniciada: se solicitó balance, carpeta tributaria e informe DICOM a {{destino}}.',
        { anio, destino: contacto?.name ?? t('la empresa') },
      ),
    );
  };

  return (
    <div className="space-y-5">
      {/* (a) Banner de estado agregado */}
      {!sinAviso && <AvisoCumplimiento resumen={resumen} motivos={motivos} />}

      {/* (b) + (c) Cuenta corriente + carpeta */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card padding="md" className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-fg-muted" aria-hidden="true" />
              <CardTitle>{t('Cuenta corriente comercial')}</CardTitle>
            </div>
            <Badge tone={claseTone}>
              {t('Clasificación {{clase}}', { clase: cuenta.clasificacion })}
            </Badge>
          </CardHeader>
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div>
              <dt className="text-xs text-fg-muted">{t('Límite aprobado')}</dt>
              <dd className="mt-0.5 text-sm font-semibold tabular-nums text-fg">
                {formatCLP(cuenta.limite)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-fg-muted">{t('Utilizado')}</dt>
              <dd className="mt-0.5 text-sm font-semibold tabular-nums text-fg">
                {formatCLP(cuenta.utilizado)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-fg-muted">{t('Condiciones')}</dt>
              <dd className="mt-0.5 text-sm text-fg">{t(cuenta.condicionesPago)}</dd>
            </div>
            <div>
              <dt className="text-xs text-fg-muted">{t('KAM · riesgo')}</dt>
              <dd className="mt-0.5 text-sm text-fg">
                {t(cuenta.ejecutivo)} · {t(cuenta.analista)}
              </dd>
            </div>
          </dl>
          <CreditoBar limite={cuenta.limite} utilizado={cuenta.utilizado} detalle className="mt-3" />
          {resumen.estado === 'bloqueado' && (
            <p className="mt-3 flex items-start gap-1.5 text-sm text-status-error">
              <Ban className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              {t(
                'Hay documentación obligatoria vencida: regulariza la carpeta antes de revisar el cupo de esta cuenta.',
              )}
            </p>
          )}
          <PendingBackendNotice className="mt-3">
            {t('Cuenta corriente — en construcción')}
          </PendingBackendNotice>
        </Card>

        <Card padding="md">
          <CardHeader>
            <div className="flex items-center gap-2">
              <BadgeCheck className="h-4 w-4 text-fg-muted" aria-hidden="true" />
              <CardTitle>{t('Carpeta documental')}</CardTitle>
            </div>
          </CardHeader>
          <CumplimientoBar conformes={resumen.conformes} total={resumen.obligatorios} />
          <p className="mt-2 text-sm text-fg-muted">
            {t('{{x}} de {{y}} documentos obligatorios vigentes.', {
              x: resumen.conformes,
              y: resumen.obligatorios,
            })}
          </p>
          <div className="mt-3 border-t border-border pt-3">
            <div className="text-xs text-fg-muted">{t('Plantilla aplicada')}</div>
            <div className="mt-0.5 text-sm font-medium text-fg">{t(PLANTILLA_NOMBRE)}</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Badge tone="brand">{t('Preset Chile')}</Badge>
              <Badge tone="neutral">{t('Front-only')}</Badge>
            </div>
          </div>
        </Card>
      </div>

      {/* (d) Checklist documental */}
      <Card padding="md">
        <CardHeader>
          <div>
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-fg-muted" aria-hidden="true" />
              <CardTitle>{t('Requisitos documentales')}</CardTitle>
            </div>
            <CardDescription className="mt-0.5">
              {t('Reglas para clientes con cuenta corriente en Chile.')}
            </CardDescription>
          </div>
        </CardHeader>
        <PendingBackendNotice className="mb-3">
          {t('Los estados documentales se registran en este navegador')}
        </PendingBackendNotice>
        <Table>
          <THead>
            <tr>
              <TH>{t('Documento')}</TH>
              <TH>{t('Regla')}</TH>
              <TH>{t('Estado')}</TH>
              <TH>{t('Acciones')}</TH>
            </tr>
          </THead>
          <TBody>
            {resumen.items.map((item) => {
              const esContrato = item.docTipoId === DOC_CONTRATO;
              return (
                <TR key={item.docTipoId} className={cn(item.bloquea && 'bg-status-error-soft/40')}>
                  <TD>
                    <div className="font-medium text-fg">{t(item.nombre)}</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {item.obligatorio ? (
                        <Badge tone="brand">{t('Obligatorio')}</Badge>
                      ) : (
                        <Badge tone="neutral">{t('Opcional')}</Badge>
                      )}
                      {item.regla.bloqueoAlVencer && (
                        <Badge tone="error">{t('Bloquea al vencer')}</Badge>
                      )}
                      {item.regla.requiereFirma && <Badge tone="info">{t('Requiere firma')}</Badge>}
                    </div>
                  </TD>
                  <TD className="text-fg-muted">{textoRegla(item.regla, t)}</TD>
                  <TD>
                    <div className="flex flex-col items-start gap-1">
                      <VencimientoPill venceAt={item.venceAt} estado={item.estado} />
                      {esContrato && contract?.dateSigned && (
                        <Badge tone="success" dot>
                          {t('Firmado {{fecha}}', { fecha: formatDate(contract.dateSigned) })}
                        </Badge>
                      )}
                      {item.doc?.solicitadoAt && (
                        <span className="text-[11px] text-fg-muted">
                          {t('Solicitado al cliente')}
                        </span>
                      )}
                    </div>
                  </TD>
                  <TD>
                    {esContrato ? (
                      contratoReal ? (
                        <div className="flex flex-wrap gap-1.5">
                          {onRenewContract && (
                            <Button
                              // Vencido o por vencer: renovar es la acción que corresponde.
                              variant={
                                item.estado === 'vencido' || item.estado === 'por_vencer'
                                  ? 'primary'
                                  : 'outline'
                              }
                              size="sm"
                              iconLeft={<RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />}
                              onClick={onRenewContract}
                            >
                              {t('Renovar')}
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            iconLeft={<FileSignature className="h-3.5 w-3.5" aria-hidden="true" />}
                            onClick={() => setVerContrato(true)}
                          >
                            {t('Ver contrato')}
                          </Button>
                        </div>
                      ) : (
                        <PendingBackendNotice>
                          {t('El contrato se crea desde el alta de empresa')}
                        </PendingBackendNotice>
                      )
                    ) : item.estado === 'en_revision' ? (
                      <div className="flex flex-wrap gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          iconLeft={<BadgeCheck className="h-3.5 w-3.5" aria-hidden="true" />}
                          onClick={() => aprobar(item)}
                        >
                          {t('Aprobar')}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => rechazar(item)}>
                          {t('Rechazar')}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          iconLeft={<Send className="h-3.5 w-3.5" aria-hidden="true" />}
                          onClick={() => solicitar(item)}
                        >
                          {t('Solicitar')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          iconLeft={<Upload className="h-3.5 w-3.5" aria-hidden="true" />}
                          onClick={() => subir(item)}
                        >
                          {t('Subir')}
                        </Button>
                      </div>
                    )}
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
        <p className="mt-3 text-xs text-fg-muted">
          {t(
            'La carga de archivos es referencial: el documento queda en revisión y se aprueba o rechaza desde esta misma tabla. Todo se registra en este navegador.',
          )}
        </p>
      </Card>

      {/* (e) Evaluaciones crediticias */}
      <Card padding="md">
        <CardHeader>
          <div>
            <div className="flex items-center gap-2">
              <Gavel className="h-4 w-4 text-fg-muted" aria-hidden="true" />
              <CardTitle>{t('Evaluaciones crediticias')}</CardTitle>
            </div>
            <CardDescription className="mt-0.5">
              {t(
                'Se renueva todos los años. De su resultado sale el límite de crédito y el plazo de pago.',
              )}
            </CardDescription>
          </div>
          <Button
            variant={resumen.estado === 'bloqueado' ? 'primary' : 'outline'}
            size="sm"
            iconLeft={<CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />}
            onClick={iniciarEvaluacion}
          >
            {resumen.estado === 'bloqueado'
              ? t('Renovar evaluación {{anio}}', { anio })
              : t('Iniciar evaluación {{anio}}', { anio })}
          </Button>
        </CardHeader>
        {resumen.evaluacion?.venceAt && (
          <div className="mb-3 flex flex-wrap items-center gap-2 text-sm text-fg-muted">
            <span>{t('Vigencia de la evaluación actual:')}</span>
            <VencimientoPill
              venceAt={resumen.evaluacion.venceAt}
              estado={resumen.evaluacion.estado}
            />
            <span className="tabular-nums">({formatDate(resumen.evaluacion.venceAt)})</span>
          </div>
        )}
        <Table>
          <THead>
            <tr>
              <TH>{t('Período')}</TH>
              <TH>{t('Fecha')}</TH>
              <TH>{t('Resultado')}</TH>
              <TH>{t('Clasif.')}</TH>
              <TH>{t('Límite aprobado')}</TH>
              <TH>{t('Analista')}</TH>
            </tr>
          </THead>
          <TBody>
            <TR>
              <TD colSpan={6}>
                <div className="space-y-2 py-1">
                  <span className="text-fg-muted">
                    {t('Sin evaluaciones registradas: el cliente opera en prepago.')}
                  </span>
                  <PendingBackendNotice />
                </div>
              </TD>
            </TR>
          </TBody>
        </Table>
      </Card>

      {/* (f) Usuarios del portal de clientes (lista real; se administra en
          Empresas → Cuentas, onGoCuentas salta allá con el filtro aplicado) */}
      <UsuariosEmpresaSection contract={contract} onGoCuentas={onGoCuentas} />

      {/* (g) Nota final */}
      <p className="flex items-start gap-1.5 text-xs text-fg-muted">
        <UserCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {t('Cambiar una regla recalculará el cumplimiento de toda la cartera.')}
      </p>

      {/* Drawer "Ver contrato" con los datos reales del contract */}
      <Drawer
        open={verContrato}
        onClose={() => setVerContrato(false)}
        size="md"
        title={t('Contrato marco')}
        description={t('Datos del contrato vigente.')}
        footer={
          <Button variant="ghost" onClick={() => setVerContrato(false)}>
            {t('Cerrar')}
          </Button>
        }
      >
        <div className="space-y-2">
          <KV
            label={t('Empresa')}
            value={contract?.companyId?.name}
          />
          <KV label={t('RUT')} value={contract?.companyId?.businessIdentifier} mono />
          <KV
            label={t('Firmado')}
            value={contract?.dateSigned ? formatDate(contract.dateSigned) : ''}
          />
          <KV
            label={t('Inicio de vigencia')}
            value={
              contract?.validityPeriod?.startDate
                ? formatDate(contract.validityPeriod.startDate)
                : ''
            }
          />
          <KV
            label={t('Fin de vigencia')}
            value={
              contract?.validityPeriod?.endDate ? formatDate(contract.validityPeriod.endDate) : ''
            }
          />
          <KV label={t('Estado')} value={contract?.status} />
          <KV
            label={t('Bloqueo de pago')}
            value={contract?.paymentBlock?.isBlocked ? t('Sí') : t('No')}
          />
          <KV
            label={t('Integraciones')}
            value={
              Array.isArray(contract?.integrations) && contract.integrations.length
                ? String(contract.integrations.length)
                : ''
            }
          />
        </div>
        {/* Copias firmadas del contrato (PDF en S3, descarga autenticada por contracts). mt-6 y
            no mt-5: el `.mt-5` de Bootstrap (global, con !important) lo pisa con 3rem. */}
        <section className="mt-6 border-t border-border pt-4">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-fg-muted">
            {t('Contratos firmados')}
          </h3>
          <SignedContractsList
            className="mt-2"
            contractId={contract?._id}
            documents={contract?.signedDocuments}
          />
        </section>
      </Drawer>
    </div>
  );
}
